import { CompanyReviewCard_userReview$key } from '#app/__generated__/relay/CompanyReviewCard_userReview.graphql.js';
import { Avatar } from '#app/components/Avatars/Avatar/Avatar.js';
import { Button } from '#app/components/Buttons/Button.js';
import { BlackStarIcon } from '#app/components/companyProfilesV2/components/CompanyRecommendations/AverageCompanyReviewScoreBreakdown.js';
import { Link } from '#app/components/Link/index.js';
import { Text } from '#app/components/Primitives/Text.js';
import { formatName } from '#app/utilities/formatName.js';
import { dayjs } from '@contra/date';
import { ChevronDownIcon, ChevronUpIcon } from '@contra/icons/icons';
import { graphql, useFragment } from '@contra/relay';
import { getRoute } from '@contra/routes';
import { useCallback, useState } from 'react';
import { css, styled } from 'styled-components';

const Container = styled.div`
  ${({ theme }) => css`
    padding: 30px 0;
    &:not(:last-of-type) {
      border-bottom: 1px solid ${theme.colorsV2.gray30};
    }
    display: flex;
    flex-direction: column;
    gap: 16px;
  `}
`;
const Body = styled(Text).attrs({
  textStyle: 'bodySmall',
})``;
const Header = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-direction: row;
  flex-wrap: wrap;
`;

const Divider = styled.div`
  ${({ theme }) => css`
    background-color: ${theme.colorsV2.gray40};
    width: 1.5px;
    height: 16px;
  `}
`;

const OtherReviewContainer = styled.div`
  margin-top: 40px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const RecommendedText = styled(Text).attrs({
  textStyle: 'subtitleSmall',
})`
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Footer = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: space-between;
`;

const ProjectOwner = styled(Link)`
  display: flex;
  gap: 4px;
  align-items: center;
`;

const Author = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const AuthorDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-weight: 600;
`;

const AuthorMetadata = styled(Text)`
  font-weight: 400;
  color: ${({ theme }) => theme.colorsV2.gray60};
`;

export const CompanyReviewCard = ({
  hideOtherReviews,
  hideRecipient,
  reference,
}: {
  readonly hideOtherReviews?: boolean;
  readonly hideRecipient?: boolean;
  readonly reference: CompanyReviewCard_userReview$key;
}) => {
  const [reviewsAreVisible, setReviewsAreVisible] = useState(false);
  const data = useFragment(
    graphql`
      fragment CompanyReviewCard_userReview on UserReview {
        id
        reviewText
        rating
        isPositiveReview
        createdAt

        otherReviewsBySameAuthor {
          id
          reviewText
          createdAt
        }

        author {
          id
          displayUsername
          lastName
          firstName
          avatarImage {
            url
            id
            uid
          }
        }
        associatedWork {
          paidProjectProfile {
            id
            client {
              displayUsername

              lastName
              firstName

              avatarImage {
                url
                id
                uid
              }
            }
            contractor {
              id
            }
          }
        }
      }
    `,
    reference,
  );

  const onClickViewMoreReviews = useCallback(() => {
    setReviewsAreVisible(true);
  }, []);

  const onClickHideMoreReviews = useCallback(() => {
    setReviewsAreVisible(false);
  }, []);

  const dateFormatter = dayjs(data?.createdAt);

  if (!data.isPositiveReview) {
    return null;
  }

  const authorFullName = formatName(
    { firstName: data.author.firstName, lastName: data.author.lastName },
    'fullName',
  );

  const clientOwnerFullName = data.associatedWork.paidProjectProfile
    ? formatName(
        {
          firstName: data.associatedWork.paidProjectProfile.client.firstName,
          lastName: data.associatedWork.paidProjectProfile.client.lastName,
        },
        'fullName',
      )
    : '';

  const authorRoleOnProject = data.associatedWork.paidProjectProfile
    ? data.associatedWork.paidProjectProfile.contractor.id === data.author.id
      ? 'Freelancer'
      : 'Client'
    : 'Client';

  const otherReviews = data.otherReviewsBySameAuthor ?? [];
  const hasOtherReviews = otherReviews.length;

  return (
    <Container aria-label={`Recommendation from ${authorFullName}`}>
      {!hideRecipient && (
        <Header>
          <RecommendedText>
            <BlackStarIcon />
            Recommended
          </RecommendedText>
          {data.associatedWork.paidProjectProfile?.id ? (
            <>
              <Divider />
              <ProjectOwner
                to={getRoute('/@username', {
                  args: {
                    username:
                      data.associatedWork.paidProjectProfile.client
                        .displayUsername,
                  },
                })}
              >
                <Avatar
                  avatarImage={
                    data.associatedWork.paidProjectProfile?.client?.avatarImage
                  }
                  fallbackName={clientOwnerFullName}
                  size="xxs"
                />
                <Text
                  color="gray80"
                  textStyle="subtitleSmall"
                >
                  {clientOwnerFullName}
                </Text>
              </ProjectOwner>
            </>
          ) : null}
        </Header>
      )}
      {data.reviewText ? <Body>{data.reviewText}</Body> : null}
      <Footer>
        <Author>
          <Link
            to={getRoute('/@username', {
              args: {
                username: data.author.displayUsername,
              },
            })}
          >
            <Avatar
              avatarImage={data.author.avatarImage}
              fallbackName={authorFullName}
              size="sm"
            />
          </Link>
          <AuthorDetails>
            <Link
              to={getRoute('/@username', {
                args: {
                  username: data.author.displayUsername,
                },
              })}
            >
              <Text textStyle="subtitleSmall">{authorFullName}</Text>
            </Link>
            <AuthorMetadata textStyle="bodySmall">
              {`${authorRoleOnProject} • ${dateFormatter.format(
                'MMM D, YYYY',
              )}`}
            </AuthorMetadata>
          </AuthorDetails>
        </Author>
        {hasOtherReviews && !hideOtherReviews ? (
          <Button
            icon={reviewsAreVisible ? ChevronUpIcon : ChevronDownIcon}
            iconPlacement="right"
            onClick={
              reviewsAreVisible
                ? onClickHideMoreReviews
                : onClickViewMoreReviews
            }
            size="small"
            variant="V2Link"
          >
            {otherReviews.length} other review
            {otherReviews.length > 1 ? 's' : ''} from {data.author.firstName}
            {hideRecipient
              ? null
              : `to ${
                  data.associatedWork.paidProjectProfile?.client.firstName ??
                  'Client'
                }`}
          </Button>
        ) : null}
      </Footer>
      {hasOtherReviews && reviewsAreVisible ? (
        <>
          {otherReviews.map((review) => (
            <OtherReviewContainer key={review.id}>
              <Header>
                <RecommendedText>
                  <BlackStarIcon />
                  Recommended
                </RecommendedText>
              </Header>
              {review.reviewText ? <Body>{review.reviewText}</Body> : null}
              <Footer>
                <Author>
                  <AuthorDetails>
                    <AuthorMetadata textStyle="bodySmall">
                      {`${authorRoleOnProject} • ${dayjs(
                        review?.createdAt,
                      ).format('MMM D, YYYY')}`}
                    </AuthorMetadata>
                  </AuthorDetails>
                </Author>
              </Footer>
            </OtherReviewContainer>
          ))}
        </>
      ) : null}
    </Container>
  );
};
